/**
 * Classify an R1C1 formula by its structural intent.
 *
 * v1 scope: SUM(...) only. We look for the first top-level (outside string
 * literals + quoted sheet names) `SUM(` call, parse its argument list for
 * R1C1 references, and check the directional pattern of the references'
 * row/column offsets relative to the cell holding the formula.
 *
 * Direction rules:
 *   - All non-zero row offsets negative AND all col offsets zero    → fromAbove
 *   - All non-zero row offsets positive AND all col offsets zero    → fromBelow
 *   - All non-zero col offsets negative AND all row offsets zero    → fromLeft
 *   - All non-zero col offsets positive AND all row offsets zero    → fromRight
 *   - Any absolute row/col reference (e.g. R5 or C7 without brackets) → mixed
 *   - Both row and col offsets non-zero, OR mixed signs              → mixed
 *
 * Future extensions can add `average.from*`, `count.from*`, `lookup.*`,
 * `arithmetic.simple`, etc. with the same `family.variant` naming scheme.
 */

import type { FormulaKindRule, FormulaShape } from "./kindRules";
import { matchKindRules } from "./kindRules";

export type FormulaKind = string;

export const FORMULA_KINDS = {
  SUM_FROM_ABOVE: "sum.fromAbove",
  SUM_FROM_BELOW: "sum.fromBelow",
  SUM_FROM_LEFT: "sum.fromLeft",
  SUM_FROM_RIGHT: "sum.fromRight",
  SUM_MIXED: "sum.mixed",
  UNKNOWN: "unknown",
} as const;

/**
 * Classify a formula's structural intent.
 *
 * Order of precedence:
 *   1. User-defined regex rules (if `userRules` provided) — first match wins,
 *      subject to each rule's shape restriction (or no restriction)
 *   2. Built-in SUM directional classifier (sum.fromAbove / Below / Left / Right / mixed)
 *   3. "unknown" fallback
 */
export function classifyFormula(
  formula: string,
  shape: FormulaShape,
  userRules?: FormulaKindRule[]
): FormulaKind {
  if (userRules && userRules.length > 0) {
    const userKind = matchKindRules(formula, shape, userRules);
    if (userKind !== null) return userKind;
  }

  const sumOpen = findFunctionOpen(formula, "SUM");
  if (sumOpen === -1) return FORMULA_KINDS.UNKNOWN;

  const args = extractArgs(formula, sumOpen);
  if (args === null) return FORMULA_KINDS.UNKNOWN;

  return classifySumArgs(args);
}

interface RcRef {
  rowOffset: number | null;
  colOffset: number | null;
  rowAbsolute: boolean;
  colAbsolute: boolean;
}

function classifySumArgs(args: string): FormulaKind {
  // Strict rule: classify only when SUM has EXACTLY ONE argument, and that
  // argument is a range (a single top-level colon, no top-level commas).
  // Multi-arg SUMs like SUM(R[-5]C, R[-3]C) or single-cell SUM(R[-1]C) are
  // structurally not directional totals — call them mixed.
  if (hasTopLevelComma(args)) return FORMULA_KINDS.SUM_MIXED;

  const colonIdx = findTopLevelColon(args);
  if (colonIdx === -1) return FORMULA_KINDS.SUM_MIXED;

  // Must be exactly ONE colon at top level.
  if (findTopLevelColon(args, colonIdx + 1) !== -1) return FORMULA_KINDS.SUM_MIXED;

  const startStr = args.substring(0, colonIdx);
  const endStr = args.substring(colonIdx + 1);

  const startRefs = parseRefs(startStr);
  const endRefs = parseRefs(endStr);
  if (startRefs.length !== 1 || endRefs.length !== 1) return FORMULA_KINDS.SUM_MIXED;

  const start = startRefs[0];
  const end = endRefs[0];

  // Any absolute ref (Rn / Cn without brackets) breaks directional semantics.
  if (start.rowAbsolute || start.colAbsolute || end.rowAbsolute || end.colAbsolute) {
    return FORMULA_KINDS.SUM_MIXED;
  }

  const startRow = start.rowOffset ?? 0;
  const startCol = start.colOffset ?? 0;
  const endRow = end.rowOffset ?? 0;
  const endCol = end.colOffset ?? 0;

  // Pure vertical range: both endpoints have col offset 0 (RC), rows differ.
  if (startCol === 0 && endCol === 0) {
    if (startRow < 0 && endRow < 0) return FORMULA_KINDS.SUM_FROM_ABOVE;
    if (startRow > 0 && endRow > 0) return FORMULA_KINDS.SUM_FROM_BELOW;
    return FORMULA_KINDS.SUM_MIXED;
  }

  // Pure horizontal range: both endpoints have row offset 0, cols differ.
  if (startRow === 0 && endRow === 0) {
    if (startCol < 0 && endCol < 0) return FORMULA_KINDS.SUM_FROM_LEFT;
    if (startCol > 0 && endCol > 0) return FORMULA_KINDS.SUM_FROM_RIGHT;
    return FORMULA_KINDS.SUM_MIXED;
  }

  // Diagonal range, or mixed shifted refs.
  return FORMULA_KINDS.SUM_MIXED;
}

/**
 * Walk `s` looking for a top-level comma (not inside parens, brackets,
 * strings, or quoted sheet names). Returns true on first match.
 */
function hasTopLevelComma(s: string): boolean {
  const len = s.length;
  let depth = 0;
  let i = 0;
  while (i < len) {
    const c = s[i];
    if (c === '"') {
      i = skipString(s, i);
      continue;
    }
    if (c === "'") {
      i = skipQuotedSheet(s, i);
      continue;
    }
    if (c === "[") {
      while (i < len && s[i] !== "]") i++;
      if (i < len) i++;
      continue;
    }
    if (c === "(") {
      depth++;
      i++;
      continue;
    }
    if (c === ")") {
      depth--;
      i++;
      continue;
    }
    if (c === "," && depth === 0) return true;
    i++;
  }
  return false;
}

/**
 * Return the index of the first top-level `:` in `s` starting at `from`
 * (default 0). Returns -1 if none.
 */
function findTopLevelColon(s: string, from = 0): number {
  const len = s.length;
  let depth = 0;
  let i = from;
  while (i < len) {
    const c = s[i];
    if (c === '"') {
      i = skipString(s, i);
      continue;
    }
    if (c === "'") {
      i = skipQuotedSheet(s, i);
      continue;
    }
    if (c === "[") {
      while (i < len && s[i] !== "]") i++;
      if (i < len) i++;
      continue;
    }
    if (c === "(") {
      depth++;
      i++;
      continue;
    }
    if (c === ")") {
      depth--;
      i++;
      continue;
    }
    if (c === ":" && depth === 0) return i;
    i++;
  }
  return -1;
}

/**
 * Find the index of the `(` that opens the first call to `name` inside the
 * formula (case-insensitive). Returns -1 if not found.
 */
function findFunctionOpen(formula: string, name: string): number {
  const upper = name.toUpperCase();
  const len = formula.length;
  let i = formula.startsWith("=") ? 1 : 0;
  while (i < len) {
    const c = formula[i];
    if (c === '"') {
      i = skipString(formula, i);
      continue;
    }
    if (c === "'") {
      i = skipQuotedSheet(formula, i);
      continue;
    }
    if (/[A-Za-z_]/.test(c)) {
      let j = i;
      while (j < len && /[A-Za-z0-9._]/.test(formula[j])) j++;
      const ident = formula.substring(i, j).toUpperCase();
      let k = j;
      while (k < len && /\s/.test(formula[k])) k++;
      if (formula[k] === "(" && ident === upper) return k;
      i = j;
      continue;
    }
    i++;
  }
  return -1;
}

/**
 * Given the index of an opening `(`, return the substring of arguments
 * between it and the matching closing `)`, or null if unmatched.
 */
function extractArgs(formula: string, openParenIdx: number): string | null {
  let depth = 0;
  for (let i = openParenIdx; i < formula.length; i++) {
    const c = formula[i];
    if (c === '"') {
      i = skipString(formula, i) - 1;
      continue;
    }
    if (c === "'") {
      i = skipQuotedSheet(formula, i) - 1;
      continue;
    }
    if (c === "(") depth++;
    else if (c === ")") {
      depth--;
      if (depth === 0) return formula.substring(openParenIdx + 1, i);
    }
  }
  return null;
}

/**
 * Parse every R1C1 reference inside a substring. Walks the same way the
 * hardcode detector does — skips strings, quoted sheets, identifiers, and
 * non-R/C bracket spans (structured table refs, external workbook tags).
 */
function parseRefs(s: string): RcRef[] {
  const refs: RcRef[] = [];
  const len = s.length;
  let i = 0;
  while (i < len) {
    const c = s[i];
    if (c === '"') {
      i = skipString(s, i);
      continue;
    }
    if (c === "'") {
      i = skipQuotedSheet(s, i);
      continue;
    }
    if (c === "R" || c === "r") {
      const next = s[i + 1] ?? "";
      const isRcPair = next === "C" || next === "c";
      if (/[A-Za-z]/.test(next) && !isRcPair) {
        // Identifier (function name etc.) — skip whole token
        while (i < len && /[A-Za-z0-9._]/.test(s[i])) i++;
        continue;
      }
      const parsed = parseRcRef(s, i);
      if (parsed) {
        refs.push(parsed.ref);
        i = parsed.next;
        continue;
      }
      i++;
      continue;
    }
    if (/[A-Za-z_]/.test(c)) {
      while (i < len && /[A-Za-z0-9._]/.test(s[i])) i++;
      continue;
    }
    if (c === "[") {
      // Non-R/C bracket span (e.g., Table1[Col]) — skip to ]
      while (i < len && s[i] !== "]") i++;
      if (i < len) i++;
      continue;
    }
    i++;
  }
  return refs;
}

/**
 * Parse one R1C1 ref starting at `start` (must be R or r). Returns the
 * parsed offsets and the next index to continue scanning from, or null if
 * what's at `start` doesn't actually form a valid R-component.
 */
function parseRcRef(s: string, start: number): { ref: RcRef; next: number } | null {
  if (s[start] !== "R" && s[start] !== "r") return null;
  const len = s.length;
  let i = start + 1;
  let rowOffset: number | null = null;
  let rowAbsolute = false;

  if (i < len && s[i] === "[") {
    const close = s.indexOf("]", i);
    if (close > 0) {
      const n = parseInt(s.substring(i + 1, close), 10);
      if (!Number.isNaN(n)) rowOffset = n;
      i = close + 1;
    } else {
      return null;
    }
  } else if (i < len && /\d/.test(s[i])) {
    let j = i;
    while (j < len && /\d/.test(s[j])) j++;
    rowOffset = parseInt(s.substring(i, j), 10);
    rowAbsolute = true;
    i = j;
  }
  // else: bare R (current row)

  // Optional C component
  let colOffset: number | null = null;
  let colAbsolute = false;
  if (i < len && (s[i] === "C" || s[i] === "c")) {
    i++;
    if (i < len && s[i] === "[") {
      const close = s.indexOf("]", i);
      if (close > 0) {
        const n = parseInt(s.substring(i + 1, close), 10);
        if (!Number.isNaN(n)) colOffset = n;
        i = close + 1;
      }
    } else if (i < len && /\d/.test(s[i])) {
      let j = i;
      while (j < len && /\d/.test(s[j])) j++;
      colOffset = parseInt(s.substring(i, j), 10);
      colAbsolute = true;
      i = j;
    }
    // else: bare C (current col)
  }

  return {
    ref: { rowOffset, colOffset, rowAbsolute, colAbsolute },
    next: i,
  };
}

function skipString(s: string, startIdx: number): number {
  let i = startIdx + 1;
  while (i < s.length) {
    if (s[i] === '"') {
      if (s[i + 1] === '"') {
        i += 2;
        continue;
      }
      return i + 1;
    }
    i++;
  }
  return i;
}

function skipQuotedSheet(s: string, startIdx: number): number {
  let i = startIdx + 1;
  while (i < s.length) {
    if (s[i] === "'") {
      if (s[i + 1] === "'") {
        i += 2;
        continue;
      }
      return i + 1;
    }
    i++;
  }
  return i;
}
